import { queryOptions } from '@tanstack/react-query';
import { registerBaseURL, registerPartnersBaseURL } from '#constants/hosts';
import { camelizeKeys, client, isResponseError } from '#queries/utils';

export interface ApiRedirectionToken {
  id: string;
  ownerEmail: string;
  partnerRegistrationId: string;
}

interface ApiRedirectionTokenJsonApiResponse {
  data: {
    id: string;
    type: string;
    attributes: { owner_email?: string };
    relationships: { partner_registration: { data: { id: string } } };
  };
}

interface RedirectionTokenAttributes {
  partner_name?: string;
}

interface RedirectionTokenDetailJsonApiResponse {
  data: {
    id: string;
    attributes: RedirectionTokenAttributes;
  };
}

export type ApiRedirectionTokenData = { id: string } & ReturnType<
  typeof camelizeKeys<RedirectionTokenAttributes>
>;

function toData(response: RedirectionTokenDetailJsonApiResponse): ApiRedirectionTokenData {
  return { id: response.data.id, ...camelizeKeys(response.data.attributes) };
}

export const registerRedirectionTokensQueries = {
  all: ['register-redirection-tokens'] as const,
  /**
   * Reads a single redirection token (GET /v1/redirection_tokens/:id).
   *
   * `staleTime: Infinity` keeps the read deterministic per session — the
   * token contents don't change and refetching is wasteful.
   */
  redirectionToken: (id: string) =>
    queryOptions({
      queryKey: [...registerRedirectionTokensQueries.all, id] as const,
      staleTime: Infinity,
      queryFn: async (): Promise<ApiRedirectionToken> => {
        const response = await client<ApiRedirectionTokenJsonApiResponse>(
          `${registerBaseURL}/v1/redirection_tokens/${id}`
        );
        return {
          id: response.data.id,
          ownerEmail: response.data.attributes.owner_email ?? '',
          partnerRegistrationId: response.data.relationships.partner_registration.data.id,
        };
      },
    }),
  /**
   * Reads a redirection token's data, mirroring the Ember welcome route: try
   * the partner-register endpoint first, then fall back to the register
   * endpoint on a 404.
   *
   * `staleTime: Infinity` — the token's contents don't change per session.
   */
  detail: (id?: string) =>
    queryOptions({
      queryKey: [...registerRedirectionTokensQueries.all, 'detail', id] as const,
      staleTime: Infinity,
      retry: false,
      queryFn: async (): Promise<ApiRedirectionTokenData> => {
        if (!id) {
          throw new Error('Redirection token id is required');
        }

        try {
          return toData(
            await client<RedirectionTokenDetailJsonApiResponse>(
              `${registerPartnersBaseURL}/client_api/redirection_tokens/${id}`
            )
          );
        } catch (error) {
          if (isResponseError(error) && error.status === 404) {
            return toData(
              await client<RedirectionTokenDetailJsonApiResponse>(
                `${registerBaseURL}/v1/redirection_tokens/${id}`
              )
            );
          }
          throw error;
        }
      },
    }),
};
